import type {AutoValue, Values, RichTextContent, HideableValue } from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, autoValueCreate, autoValueIsAutoValue, hideableValueCreateHidden, hideableValueGetValue, hideableValueIsVisible } from '@wix/devzai-utils-common';
import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeBymoPageVideoSource, DoppeExternalVideoProvider} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppeBymoPageVideoSourceResolveVideoUrl,
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalVideoProviderResolveBusinessEntityReference
} from '../../client-server-common';
import type {DoppeDtoVideo} from '../../client-server-common/types/doppe-dto-video';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';

export const DoppeActionTypeVideoURLAutoSource = {
    SameAsVideoURL: 'SameAsVideoURL',
} as const;

export type DoppeActionTypeVideoURLAutoSource = Values<typeof DoppeActionTypeVideoURLAutoSource>;

export const DoppeActionTypeVideoRatio = {
    Auto: 'Auto',
    Ratio21By9: 'Ratio21By9',
    Ratio16By9: 'Ratio16By9',
    Square: 'Square',
    Ratio4By5: 'Ratio4By5',
    Ratio9By16: 'Ratio9By16',
} as const;

export type DoppeActionTypeVideoRatio = Values<typeof DoppeActionTypeVideoRatio>;

/**
 * Returns the width-to-height ratio for a given video ratio setting.
 * Returns null for Original (auto-calculated).
 */
export function doppeActionTypeVideoRatioResolveWidthHeightRatio(ratio: DoppeActionTypeVideoRatio): number | null {
    switch (ratio) {
        case DoppeActionTypeVideoRatio.Auto: return null;
        case DoppeActionTypeVideoRatio.Ratio21By9: return 21 / 9;
        case DoppeActionTypeVideoRatio.Ratio16By9: return 16 / 9;
        case DoppeActionTypeVideoRatio.Square: return 1;
        case DoppeActionTypeVideoRatio.Ratio4By5: return 4 / 5;
        case DoppeActionTypeVideoRatio.Ratio9By16: return 9 / 16;
    }
}

export namespace DoppeActionTypeVideo {

    export type DoppeActionTypeVideoURLSource = AutoValue<DoppeActionTypeVideoURLAutoSource> | string;

    export interface VideoSettings {
        externalVideoUrl: DoppeBymoPageVideoSource;
        title: HideableValue<string>;
        description: HideableValue<RichTextContent>;
        videoPlayerSettings: {
            autoplay: boolean;
            muteVideo: boolean;
        },
        videoImage: WixMediaResource | null;
        videoProvider: DoppeExternalVideoProvider | null,
        videoProviderId: string | null,
        videoRatio: DoppeActionTypeVideoRatio;
    }

    export interface ActionSettings extends
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeActionTypeVideo.VideoSettings {

    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        videoButtonText?: HideableValue<string>;
        videoButtonURL?: DoppeActionTypeVideoURLSource;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeVideo = doppeActionTypeDefineMetadata<DoppeActionTypeVideo.ActionSettings, DoppeActionTypeVideo.PartialProps>({
    id: 'io.bymo.action/video',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.externalVideoUrl = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {

        const {
            videoButtonText,
            videoButtonURL,
            ctaButton,
            ctaButtons,
            sections,
            ...restSettings
        } = action.settings;

        return {
            externalVideoUrl: '',
            videoPlayerSettings: {
                autoplay: true,
                muteVideo: false
            },
            title: hideableValueCreateHidden(''),
            description: hideableValueCreateHidden(''),
            videoImage: null,
            videoProvider: null,
            videoProviderId: null,
            videoRatio: DoppeActionTypeVideoRatio.Auto,
            videoButtonURL: autoValueCreate(DoppeActionTypeVideoURLAutoSource.SameAsVideoURL),
            videoButtonText: videoButtonText,
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: hideableValueGetValue(videoButtonText),
                showButton: hideableValueIsVisible(videoButtonText),
                url: videoButtonURL ? (autoValueIsAutoValue(videoButtonURL) ? undefined : videoButtonURL) : undefined
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...restSettings
        }
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.videoProvider !== null && actionSettings.videoProviderId !== null ?
                doppeExternalVideoProviderResolveBusinessEntityReference(
                    actionSettings.videoProvider,
                    actionSettings.videoProviderId
                ) :
                null
        ])
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const url = doppeBymoPageVideoSourceResolveVideoUrl(actionSettings.externalVideoUrl);
        return typeof actionSettings.externalVideoUrl === 'string'  ? url : doppeActionCtaButtonsResolveMainURL(actionSettings, {
            viewerLang: viewerLang,
            sourceLinkSpec: url ?? null,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            videoImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            'title': true,
            'description': true
        }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {videoProvider, videoProviderId} = actionSettings;
        if (videoProvider !== null && videoProviderId !== null) {
            const boundVideo = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalVideoProviderResolveBusinessEntityReference(videoProvider, videoProviderId)
            );
            if (boundVideo && !doppeBusinessEntityDtoIsNotFound(boundVideo)) {
                return {
                    ...actionSettings,
                    ...doppeActionTypeVideoResolveSettingsFromBoundEntity(actionSettings, boundVideo as DoppeDtoVideo),
                };
            }
        }
        return actionSettings;
    },
})

export function doppeActionTypeVideoResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeVideo.VideoSettings,
    _doppeDtoCoupon: DoppeDtoVideo
): DoppeActionTypeVideo.VideoSettings {
    return actionSettings
}

